import { Amplify } from '@spidertracks/common';
import { ServiceAbstract } from '../../ServiceAbstract';
import {
  LocationType,
  PointOfInterest,
  CreateCustomPoiResponseType,
  CustomPointOfInterest,
  PoiCodePreference,
  PoiCreateMinimumRole
} from '../../../../../common/api/spidertracks-sdk/types/PointOfInterest';

export class PoiService extends ServiceAbstract {
  public async getLocationTypes(): Promise<LocationType[]> {
    const { data } = await Amplify.API.get(
      'api.spidertracks.io-authenticated',
      `v3/location-types`
    );

    if (!data) {
      throw new Error('Error fetching location types');
    }

    return data;
  }

  public async getPois(
    organisationId: string,
    latitude: number,
    longitude: number
  ): Promise<PointOfInterest[]> {
    const { data } = await Amplify.API.post(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/points-of-interest/search`,
      {
        body: {
          page: 1,
          size: 10,
          sortBy: 'distance',
          sortDirection: 'asc',
          combiningCondition: 'or',
          filters: [
            {
              field: 'distance',
              propertyType: 'point',
              condition: 'withinRadius',
              value: {
                latitude: latitude,
                longitude: longitude,
                radius: 10000 // Unit: meter
              }
            }
          ]
        }
      }
    );

    if (!data) {
      throw new Error('Error fetching points of interest');
    }

    return data;
  }

  public async createCustomPois(
    organisationId: string | undefined,
    latitude: number | undefined,
    longitude: number | undefined,
    locationTypeName: string | undefined,
    code: string | undefined,
    name: string | undefined
  ): Promise<CreateCustomPoiResponseType | undefined> {
    if (
      organisationId === undefined ||
      latitude === undefined ||
      longitude === undefined ||
      locationTypeName === undefined ||
      code === undefined ||
      name === undefined
    ) {
      console.error('Missing required fields for creating custom points of interest', {
        organisationId,
        latitude,
        longitude,
        locationTypeName,
        code,
        name
      });
      throw new Error('Missing required fields for creating custom points of interest');
    }
    try {
      const results = await Amplify.API.post(
        'api.spidertracks.io-authenticated',
        `v3/organisations/${organisationId}/points-of-interest`,
        {
          body: {
            latitude: latitude,
            longitude: longitude,
            type: locationTypeName,
            code: code,
            name: name
          }
        }
      );
      return results;
    } catch (e) {
      console.error('Error creating custom points of interest:', e);
      throw new Error('Error creating custom points of interest');
    }
  }

  private async getCustomPoisPage(
    organisationId: string,
    page: number
  ): Promise<{ data: CustomPointOfInterest[]; totalItems: number }> {
    const { data, totalItems } = await Amplify.API.get(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/points-of-interest?page=${page}&pageSize=10`
    );

    if (!data) {
      throw new Error('Error fetching custom points of interest');
    }

    return { data, totalItems };
  }

  public async getCustomPois(
    organisationId: string,
    page: number,
    pageSize: number
  ): Promise<{ data: CustomPointOfInterest[]; totalItems: number }> {
    const { data, totalItems } = await Amplify.API.get(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/points-of-interest?page=${page}&pageSize=${pageSize}`
    );

    if (!data) {
      throw new Error('Error fetching custom points of interest');
    }

    return { data, totalItems };
  }

  public async updateCustomPoi(
    organisationId: string,
    poi: Partial<CustomPointOfInterest> & { type?: string }
  ): Promise<void> {
    if (!poi.locationId) {
      throw new Error('Poi locationId is required');
    }

    const fieldsToUpdate = Object.keys(poi).filter(
      key => key !== 'locationId' && poi[key as keyof CustomPointOfInterest] !== undefined
    );

    // Create a type-safe patch body by picking only the defined fields
    const patchBody = Object.fromEntries(
      fieldsToUpdate.map(key => [key, poi[key as keyof CustomPointOfInterest]])
    ) as Partial<Omit<CustomPointOfInterest, 'locationId'>> & { type?: string };

    await Amplify.API.patch(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/points-of-interest/${poi.locationId}`,
      {
        body: patchBody
      }
    );
  }

  public async deleteCustomPoi(organisationId: string, poiId: number): Promise<void> {
    await Amplify.API.del(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/points-of-interest/${poiId}`
    );
  }

  public async updatePoiPreferences(
    organisationId: string,
    poiCodePreference: PoiCodePreference,
    poiCreateMinimumRole: PoiCreateMinimumRole
  ): Promise<void> {
    return await Amplify.API.patch(
      'api.spidertracks.io-authenticated',
      `v3/organisations/${organisationId}/settings`,
      {
        body: { poiCodePreference, poiCreateMinimumRole }
      }
    );
  }

  public async updateFlightlogEventPoi(flightlogEventId: string, poiId: number): Promise<void> {
    // ToDo: remove this once we have the real flightlogs data
    const mockFlightlogEventId = '120393b2-b68f-4dd5-bd2a-41394bee73f8';
    try {
      await Amplify.API.patch(
        'api.spidertracks.io-authenticated',
        `v3/flightlogs/flight-events/${mockFlightlogEventId}`,
        {
          body: { locationId: poiId }
        }
      );
    } catch (e) {
      console.error(
        `Error updating poi ${poiId} for the associated flightlog event ${mockFlightlogEventId}:`,
        e
      );
      throw new Error(
        `Error updating poi ${poiId} for the associated flightlog event ${mockFlightlogEventId}`
      );
    }
  }
}
